/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { QueryClient } from '@tanstack/query-core';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React from 'react';
import {
  createTestQueryClient,
  getQueryClient,
  setQueryClient,
} from './queryClient';

interface QueryClientProviderWrapperProps {
  client?: QueryClient;
  children: React.ReactNode;
  mode?: 'test' | 'production';
}

export const QueryClientProviderWrapper = ({
  client,
  children,
  mode = 'production',
}: QueryClientProviderWrapperProps): React.ReactElement => {
  const queryClient = React.useMemo(() => {
    if (client) {
      setQueryClient(client);
      return client;
    }

    return mode === 'test' ? createTestQueryClient() : getQueryClient();
  }, [client, mode]);

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <div style={{ position: 'fixed', left: 8, bottom: '5%', zIndex: 99999 }}>
        <ReactQueryDevtools buttonPosition="relative" initialIsOpen={false} />
      </div>
    </QueryClientProvider>
  );
};
